import { Box } from "@mui/material";

import type { MetroStop as MetroStopModel, StopKind } from "../../../utils/metro-stops";
import type { MetroStopPlace } from "../../../utils/metro-layout";

import { METRO_INK, METRO_SPUR } from "./metro-theme";
import { MetroStopIcon } from "./MetroStopIcon";

type StopLook = {
    background: string;
    color: string;
    size: number;
    muted?: boolean;
    dashed?: boolean;
    shadow?: string;
};

// Labels narrower than this get a smaller title, so it wraps into fewer lines
const NARROW_LABEL = 140;

const CAPTION_COLOR:Partial<Record<StopKind, string>> = { recap: '#7A4F01', waiting: '#7A4F01', current: '#C2410C' };

export type MetroStopProps = {
    stop: MetroStopModel;
    place: MetroStopPlace;
    lineColor: string;
    selected: boolean;
    onSelect?: () => void;
};

/** One lesson on the map: a round button on its line and the title next to it. */
export function MetroStop({ stop, place, lineColor, selected, onSelect }: MetroStopProps) {
    const look = stopLook(stop, lineColor);
    // A label above grows upwards from the stop, however many lines its caption wraps into
    const labelTop = place.labelAbove ? place.y - look.size / 2 - 6 : place.y + look.size / 2 + 8;

    return <>
        <Box component="button" type="button" aria-label={ stop.label } aria-pressed={ selected }
             disabled={ !onSelect } onClick={ onSelect }
             sx={ {
                 position: 'absolute', left: place.x - look.size / 2, top: place.y - look.size / 2,
                 width: look.size, height: look.size, p: 0, boxSizing: 'border-box', borderRadius: '50%',
                 bgcolor: look.background, color: look.color, border: `3px ${ look.dashed ? 'dashed' : 'solid' } ${ METRO_INK }`,
                 boxShadow: look.shadow ?? 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                 cursor: onSelect ? 'pointer' : 'default',
                 outline: selected ? '3px solid #2065D1' : 'none', outlineOffset: 4,
                 '&:focus-visible': { outline: '3px solid #2065D1' },
                 '&:hover': onSelect ? { transform: 'scale(1.08)' } : undefined,
                 transition: 'transform 120ms',
             } }>
            <MetroStopIcon kind={ stop.kind } bonus={ stop.bonus } size={ look.size >= 52 ? 22 : look.size >= 40 ? 18 : 15 }/>
        </Box>

        <Box aria-hidden="true" sx={ {
            position: 'absolute', left: place.x - place.labelWidth / 2, top: labelTop, width: place.labelWidth, pointerEvents: 'none',
            transform: place.labelAbove ? 'translateY(-100%)' : 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', textAlign: 'center',
        } }>
            <Box component="span" sx={ { ...labelSx, fontSize: place.labelWidth < NARROW_LABEL ? 12 : 13, lineHeight: '16px', fontWeight: 800, color: look.muted ? '#4F5B67' : '#161C24' } }>
                { stop.lesson.title }
            </Box>
            { stop.caption && (
                <Box component="span" sx={ { ...labelSx, fontSize: 11, lineHeight: '14px', fontWeight: 700, color: captionColor(stop) } }>
                    { stop.caption }
                </Box>
            ) }
        </Box>
    </>;
}

const labelSx = { bgcolor: 'rgba(255,253,248,0.92)', px: 0.5, borderRadius: '4px' };

function captionColor(stop: MetroStopModel): string {
    return CAPTION_COLOR[stop.kind] ?? (stop.bonus ? '#5B3FD6' : '#637381');
}

function stopLook({ kind, bonus }: MetroStopModel, lineColor: string): StopLook {
    const size = bonus ? 34 : 42;

    switch (kind) {
        case 'done':
            return { background: bonus ? METRO_SPUR : lineColor, color: '#FFFFFF', size };
        case 'recap':
            return { background: '#FFC940', color: METRO_INK, size };
        case 'waiting':
            return { background: '#FFF3C4', color: '#7A4F01', size };
        case 'current':
            return { background: '#FFFFFF', color: '#FF6B4A', size: 52, shadow: `0 0 0 6px #FFE0D6, 0 0 0 9px ${ METRO_INK }` };
        case 'open':
            return { background: bonus ? '#ECE4FF' : '#FFFFFF', color: bonus ? METRO_SPUR : lineColor, size };
        case 'locked':
            return { background: '#FFFFFF', color: bonus ? '#8A7BD0' : '#6B7785', size, muted: true };
        case 'off':
            return { background: '#F4F6F8', color: '#6B7785', size, muted: true, dashed: true };
    }
}
